import {
  createAssessmentFromJira,
  disconnectJira,
  getJiraStatus,
  getJiraWebhookToken,
  getJiraConnectUrl,
  type JiraStatusResponse,
} from "@/modules/settings/requests/jira";
import { ZButton } from "@/shared/components/button";
import requestConfig from "@/shared/config/request";
import { ROUTES, SETTINGS_ROUTES } from "@/shared/constants/routes";
import { addNotification } from "@/shared/states/notification";
import { Button, Input } from "@nextui-org/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

const JIRA_STATUS_QUERY_KEY = ["jira", "status"] as const;
const NO_CONNECTED_JIRA_MESSAGE =
  "No connected Jira found; connect Jira first or provide credentials";

const jiraApiBaseURL = requestConfig.baseUrl;

function jiraErrorToMessage(errorCode: string): string {
  const map: Record<string, string> = {
    invalid_state: "Invalid state. Please try connecting again.",
    no_resources: "No Jira resources were authorized.",
    exchange_failed: "Authorization exchange failed. Please try again.",
  };
  return map[errorCode] ?? `Connection failed: ${errorCode}`;
}

export default function JiraIntegrationPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [ticketId, setTicketId] = useState("");
  const [webhookToken, setWebhookToken] = useState<string | null>(null);
  const [oauthBanner, setOauthBanner] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  // const handleConnectJira = () => {
  //   window.location.href = `${jiraApiBaseURL}/v0/integrations/jira/connect/redirect`;
  // };
  const handleConnectJira = async () => {
    try {
      const { authorize_url } = await getJiraConnectUrl();
        
      window.location.href = authorize_url;
    } catch (err) {
      addNotification({
        type: "error",
        title: "Failed to connect Jira",
        message: axios.isAxiosError(err)
          ? String(err.response?.data?.detail ?? err.message)
          : err instanceof Error
            ? err.message
            : "Unable to get Jira connect URL. Please try again.",
      });
    }
  };

  // Parse OAuth callback query on mount
  useEffect(() => {
    const jira = searchParams.get("jira");
    const error = searchParams.get("error");
    if (jira === "connected") {
      setOauthBanner({ type: "success", message: "Jira connected successfully" });
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete("jira");
        next.delete("error");
        return next;
      }, { replace: true });
    } else if (jira === "error" && error) {
      setOauthBanner({
        type: "error",
        message: jiraErrorToMessage(error),
      });
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete("jira");
        next.delete("error");
        return next;
      }, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const { data: status, isLoading: statusLoading } = useQuery({
    queryKey: JIRA_STATUS_QUERY_KEY,
    queryFn: getJiraStatus,
  });

  const disconnectMutation = useMutation({
    mutationFn: disconnectJira,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: JIRA_STATUS_QUERY_KEY });
      addNotification({
        type: "success",
        title: "Disconnected",
        message: "Jira disconnected successfully.",
      });
    },
    onError: (err) => {
      addNotification({
        type: "error",
        title: "Disconnect failed",
        message: axios.isAxiosError(err) ? String(err.response?.data?.detail ?? err.message) : "Disconnect failed, please try again later.",
      });
    },
  });

  const createAssessmentMutation = useMutation({
    mutationFn: (ticketId: string) =>
      createAssessmentFromJira({
        provider: "jira",
        ticket_id: ticketId,
      }),
    onSuccess: () => {
      addNotification({
        type: "success",
        title: "Assessment created",
        message: "Assessment created successfully from Jira.",
      });
      setTicketId("");
    },
    onError: (err) => {
      const detail =
        axios.isAxiosError(err) && typeof err.response?.data?.detail === "string"
          ? err.response.data.detail
          : "";
      if (detail.includes("No connected Jira found") || detail.includes("connect Jira first")) {
        addNotification({
          type: "error",
          title: "No connected Jira found",
          message: "Please connect Jira first",
        });
      } else {
        addNotification({
          type: "error",
          title: "Create assessment failed",
          message: detail || (axios.isAxiosError(err) ? err.message : "Please try again later."),
        });
      }
    },
  });

  const webhookTokenMutation = useMutation({
    mutationFn: getJiraWebhookToken,
    onSuccess: (data) => {
      if (data?.token) setWebhookToken(data.token);
    },
    onError: (err) => {
      if (axios.isAxiosError(err) && err.response?.status === 404) {
        addNotification({
          type: "info",
          title: "Coming soon",
          message: "Webhook Token interface is not yet available, please try again later.",
        });
      } else {
        addNotification({
          type: "error",
          title: "Failed to get token",
          message: axios.isAxiosError(err) ? String(err.response?.data?.detail ?? err.message) : "Please try again later.",
        });
      }
    },
  });

  const handleCreateAssessment = useCallback(() => {
    const id = ticketId.trim();
    if (!id) {
      addNotification({
        type: "warn",
        title: "Please enter Ticket ID",
        message: "Please enter Jira ticket key（ E.G PROJ-123）.",
      });
      return;
    }
    createAssessmentMutation.mutate(id);
  }, [ticketId, createAssessmentMutation]);

  const connected = status?.connected === true;

  return (
    <div className="p-6 flex flex-col gap-6 max-w-2xl">
      {oauthBanner && (
        <div
          className={
            oauthBanner.type === "success"
              ? "p-4 rounded-lg bg-green-50 border border-green-200 text-green-800"
              : "p-4 rounded-lg bg-red-50 border border-red-200 text-red-800"
          }
        >
          {oauthBanner.message}
        </div>
      )}

      {/* <div className="flex flex-col gap-2">
        <h4 className="text-sm font-medium leading-5 text-[#171717]">Jira Integration</h4>
        <p className="text-sm text-[#777780]">
          Connect Jira to create assessments and enable webhook/automation.
        </p>
      </div> */}

      <div className="flex items-start gap-4">
        <img
          src="/jira-logo.svg"
          alt="Jira"
          className="w-12 h-12 rounded-lg object-contain shrink-0"
        />
        <div className="flex flex-col gap-2">
          <h4 className="text-sm font-medium leading-5 text-[#171717]">
          Jira Integration
          </h4>
          <p className="text-sm text-[#777780]">
          Connect Jira to create assessments and enable webhook/automation.
          </p>
        </div>
      </div>

      {statusLoading ? (
        <p className="text-sm text-[#777780]">Loading connection status…</p>
      ) : (
        <div className="p-4 bg-[#FAFAFA] border border-[#E4E4E7] rounded-xl flex flex-col gap-4">
          {connected ? (
            <>
              <div className="flex flex-col gap-1">
                <span className="text-sm font-medium text-[#171717]">Connected</span>
                {(status as JiraStatusResponse).site_name && (
                  <span className="text-sm text-[#717179]">
                    Workspace: 
                    {(status as JiraStatusResponse).site_name}
                    {(status as JiraStatusResponse).site_url && (
                      <>
                        {" · "}
                        <a
                          href={(status as JiraStatusResponse).site_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary underline"
                        >
                          Open in Jira
                        </a>
                      </>
                    )}
                  </span>
                )}
              </div>
              <ZButton
                color="default"
                variant="bordered"
                className="w-fit"
                isDisabled={disconnectMutation.isPending}
                isLoading={disconnectMutation.isPending}
                onClick={() => disconnectMutation.mutate()}
              >
                Disconnect
              </ZButton>
            </>
          ) : (
            <>
              <span className="text-sm text-[#717179]">Not connected</span>
              <ZButton
                color="primary"
                className="w-fit"
                onClick={handleConnectJira}
              >
                Connect Jira
              </ZButton>
            </>
          )}
        </div>
      )}

      {connected && (
        <>
          {/* <div className="flex flex-col gap-2">
            <h4 className="text-sm font-medium leading-5 text-[#171717]">从 Jira 创建评估</h4>
            <p className="text-sm text-[#777780]">
              Input Jira ticket key（ E.G PROJ-123），use the currently connected Jira to create an assessment.
            </p>
            <div className="flex gap-2 items-end flex-wrap">
              <Input
                label="Ticket ID"
                placeholder="E.G PROJ-123"
                value={ticketId}
                onValueChange={setTicketId}
                classNames={{ base: "max-w-xs" }}
              />
              <ZButton
                color="primary"
                isDisabled={createAssessmentMutation.isPending || !ticketId.trim()}
                isLoading={createAssessmentMutation.isPending}
                onClick={handleCreateAssessment}
              >
                创建评估
              </ZButton>
            </div>
          </div> */}

          <div className="flex flex-col gap-2">
            <h4 className="text-sm font-medium leading-5 text-[#171717]">
              Generate Token
            </h4>
            <p className="text-sm text-[#777780]">
              Use this token to authenticate Jira webhook or automation requests to Zania.
            </p>
            {webhookToken ? (
              <div className="flex flex-col gap-2">
                <div className="p-3 bg-[#FAFAFA] border border-[#E4E4E7] rounded-lg font-mono text-sm break-all">
                  {webhookToken.slice(0, 20)}…{webhookToken.slice(-8)}
                </div>
                <Button
                  size="sm"
                  variant="bordered"
                  onClick={() => {
                    void navigator.clipboard.writeText(webhookToken);
                    addNotification({
                      type: "success",
                      message: "Token has been copied to the clipboard",
                    });
                  }}
                >
                  Copy
                </Button>
              </div>
            ) : (
              <ZButton
                color="primary"
                variant="bordered"
                className="w-fit"
                isDisabled={webhookTokenMutation.isPending}
                isLoading={webhookTokenMutation.isPending}
                onClick={() => webhookTokenMutation.mutate()}
              >
                Generate Token
              </ZButton>
            )}
          </div>
        </>
      )}

      <div className="pt-2">
        <Link
          to={`/${ROUTES.SETTINGS}?tab=integrations`}
          className="text-sm text-primary hover:underline"
        >
          ← Back to integrations list
        </Link>
      </div>
    </div>
  );
}
